import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import usePermission from "CustomHooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  fetchUsersType,
  updateStatusInUsersTypes
} from "slices/userstypes/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import AddUserTypeModal from "./AddUserTypeModal";

const UsersTypeList = () => {
  document.title = "User Type List";
  const dispatch = useDispatch<any>();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "userType",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
  } = tableState;

  const selectorganisation = createSelector(
    (state: any) => state.usersType, // ✅ use lowercase to match your store setup
    (userTypesState) => ({
      organisation: userTypesState?.usersTypesList ?? [],
      rowCount: userTypesState?.rowCount ?? 0,
      loading: userTypesState?.loading ?? false,
      error: userTypesState?.error ?? null,
    })
  );
  const { organisation, rowCount, loading, error } =
    useSelector(selectorganisation);

  // Use custom hook to fetch and access permissions
  const { canCreate, canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.user_types
  );

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState<any>({});

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Convert filterValue (filterField/filterValue) to ColumnFiltersState (id/value) format
  const convertedFilters = useMemo(() => {
    if (!colValue || !Array.isArray(colValue)) return [];
    return colValue.map((f: any) => ({
      id: f.filterField || f.id,
      value: f.filterValue !== undefined ? f.filterValue : f.value,
    }));
  }, [colValue]);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  // Fetch data when search, filters, sorting, etc. change
  useEffect(() => {
    dispatch(
      fetchUsersType({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: convertedFilters,
      })
    );
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, search, convertedFilters]);

  // Handle status toggle
  const handleStatusToggle = useCallback(async (usertype: any) => {
    try {
      const updatedStatus = usertype.is_active === true ? false : true;
      const update_payload = { ...usertype, is_active: updatedStatus };
      await dispatch(updateStatusInUsersTypes(update_payload))
        .unwrap()
        .then(() => {
          toast.success(
            `UserType ${
              updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });

      dispatch(
        fetchUsersType({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: convertedFilters,
        })
      );
    } catch (err) {
      toast.error("Failed to update status");
    }
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, search, convertedFilters]);

  const handleEdit = (rowData: any) => {
    setEditData(rowData); // ✅ Pass data to modal
    setShowModal(true); // Open modal in EDIT mode
  };

  //*// Handle Create handled in handle Edit
  // const handleCreate = () => {
  //   setEditData(null);         // ✅ Clear previous editData
  //   setShowModal(true);        // Open modal in CREATE mode
  // };

  const handleClose = () => {
    setShowModal(false);
    setEditData(null); // ✅ Clear edit data on close
  };

  const DynamicAction = useMemo(() =>
    canUpdate
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => (
              <EditButton onClick={() => handleEdit(row?.original)} />
            ),
          },
        ]
      : [],
    [canUpdate]
  );

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: " User Type",
        accessorKey: "user_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/user-types/dropdown/user_type",
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableSorting: false, // Disables sorting
        enableColumnFilter: false, // Disables filtering
        cell: ({ row }: { row: any }) => {
          const userType = row.original;
          return (
            <StatusToggle
              isActive={userType.is_active}
              onChange={() => {
                handleStatusToggle(userType);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [
      handleStatusToggle,
      canUpdate,
      DynamicAction,
      pageIndex,
      pageSize,
    ]
  );

  return (
    <ListingPageContainer>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Users Type List</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {canCreate && (
                          <ActionButton
                            label="Create User Type"
                            icon="bi bi-plus-circle"
                            variant="primary"
                            handleClick={() => setShowModal(true)}
                          />
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={
                        organisation && organisation.length > 0
                          ? organisation
                          : []
                      }
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search organisation..."
                    />
                  </LoaderErrorWrapper>
            

                  <AddUserTypeModal
                    key={editData?.user_type_id || "create"} // forces fresh instance
                    show={showModal}
                    handleClose={handleClose}
                    onSuccess={() => {
                      dispatch(
                        fetchUsersType({
                          page: pageIndex + 1,
                          pageSize,
                          sort: sortField,
                          order: sortField ? sortDirection : "",
                          search,
                          filters: colValue,
                        })
                      );
                    }}
                    editData={editData}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
    </ListingPageContainer>
  );
};

export default UsersTypeList;
